import { useState } from 'react';
import type { CreatedInvitation, EmailResult } from '../api';

/**
 * One minted invitation link, and the three facts an admin has to know before they paste it.
 *
 * This used to be a block inside `InviteDialog`. It is its own component now because the
 * new-group flow mints several links at once and has to show all of them — and a *second*
 * rendering of a live invitation link is the one thing this product must not grow. The
 * sentence beside the link is load-bearing: once, for that address, for seven days. An admin
 * who does not know those three things will paste it into a staff channel, and a second copy
 * of this presentation is how one of the three quietly goes missing from one of the screens.
 *
 * What it says depends on whether Collab emailed the link (R4, R6):
 *
 * - **`sent`** — SendGrid accepted it. The colleague has the link already, so the token stays
 *   off screen behind "Copy link instead". Revealed, it carries the same three facts: a link
 *   copied from here is as forwardable as one copied before email existed.
 * - **Anything else** — the admin holds the only copy. The copy says so, and says *why* no
 *   email went out, because "link only" with no reason reads as Collab forgetting to send it.
 *
 * `.collab-invite-dialog` is a replay block selector (`analytics/client.ts`), so every caller
 * renders this inside an element carrying that class: what is on screen here is an address and
 * a working token at the same time, and a recording of it is a recording of a live invitation.
 */

export interface InviteLinkProps {
  invitation: CreatedInvitation;
  emailResult: EmailResult;
}

/** Why no email went out. Each is a fact about the address or the Worker, never a guess. */
const LINK_ONLY_COPY: Readonly<Record<Exclude<EmailResult, 'sent'>, string>> = {
  failed: 'Link only — email failed.',
  not_configured: 'Link only — email is not set up.',
  disabled: 'Link only — email is switched off.',
};

export default function InviteLink({ invitation, emailResult }: InviteLinkProps) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(invitation.link);
      setCopied(true);
    } catch {
      // Clipboard access is refused often enough — an insecure origin, a permission prompt
      // dismissed — that a failure has to be a stated one. The link is on screen either way.
      setCopied(false);
    }
  }

  const facts = (
    <>
      It works <strong>once</strong>, only for <strong>{invitation.email}</strong>, and only
      for <strong>seven days</strong> — until {new Date(invitation.expiresAt).toLocaleDateString()}.
    </>
  );

  const link = (
    <>
      <code>{invitation.link}</code>
      {/*
        * "Copy link" on screen, the address in the accessible name: several of these can be on
        * one page at once, and three buttons all called "Copy link" is three buttons nobody
        * using a screen reader can tell apart. The visible text is a prefix of the accessible
        * name, so voice control can still say "Copy link".
        */}
      <button type="button" aria-label={`Copy link for ${invitation.email}`} onClick={() => void copy()}>
        Copy link
      </button>
      {copied && <span role="status"> Copied.</span>}
    </>
  );

  if (emailResult === 'sent') {
    return (
      <div className="invite-link is-emailed">
        <p><strong>Emailed to {invitation.email}.</strong></p>
        <button
          type="button"
          className="secondary"
          aria-expanded={revealed}
          aria-label={`Copy link instead for ${invitation.email}`}
          onClick={() => setRevealed((open) => !open)}
        >
          Copy link instead
        </button>
        {revealed && (
          <>
            <p>{facts}</p>
            {link}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="invite-link">
      <p><strong>{LINK_ONLY_COPY[emailResult]}</strong></p>
      <p>
        <strong>Send this link to {invitation.email} yourself.</strong> {facts}
      </p>
      {link}
    </div>
  );
}
